import moment from "moment";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getVariablesFromUrlRaw, updateUrlParams } from "state/url_params/utils";

interface QueryStringSerializer<T> {
  toQueryString: (i: T) => string | string[];
  fromQueryString: (i: string | string[]) => T;
}

export const StringSerializer = {
  toQueryString: (i: string) => i,
  fromQueryString: (i: string | string[]) => (Array.isArray(i) ? i[0] : i),
};

export const StringArraySerializer = {
  toQueryString: (i: string[]) => i,
  fromQueryString: (i: string | string[]) => {
    return typeof i === "string" ? [i] : i;
  },
};

export const SplitStringArraySerializer = {
  toQueryString: (i: string[]) => i.join(","),
  fromQueryString: (i: string | string[]) => (Array.isArray(i) ? i : i.split(",")),
};

export const MomentSerializer = {
  toQueryString: (i: moment.Moment) => i.toISOString(),
  fromQueryString: (i: string | string[]) => moment(i),
};

export function useQueryStringState(
  parameter: string
): [string | undefined, React.Dispatch<React.SetStateAction<string | undefined>>];

export function useQueryStringState<T = string | undefined>(
  parameter: string,
  defaultValue?: T,
  serializer?: QueryStringSerializer<T>
): [T, React.Dispatch<React.SetStateAction<T>>];

export function useQueryStringState<T = string>(
  parameter: string,
  defaultValue?: T,
  serializer?: QueryStringSerializer<T>
): [T | undefined, React.Dispatch<React.SetStateAction<T | undefined>>] {
  if (serializer === undefined) {
    serializer = StringSerializer as any;
  }

  const variables = useSelector(getVariablesFromUrlRaw);
  const dispatch = useDispatch();

  const value = useMemo(
    () =>
      parameter in variables ? serializer?.fromQueryString(variables[parameter]) : defaultValue,
    [parameter, variables, serializer, defaultValue]
  );
  const setValue = useCallback(
    (newValue: React.SetStateAction<T | undefined>) => {
      const _newValue = newValue instanceof Function ? newValue(value) : newValue;

      dispatch(
        updateUrlParams({
          [parameter]: _newValue !== undefined ? serializer?.toQueryString(_newValue) : undefined,
        })
      );
    },
    [dispatch, parameter, serializer, value]
  );

  return [value, setValue];
}
